import React from 'react';
import { AlertCircle, CheckCircle2, Clock, Loader2, WifiOff, X } from 'lucide-react';

const BYTES_PER_MB = 1024 * 1024;
const WHOLE_MB_FROM = 10;
const SECONDS_PER_MINUTE = 60;
const FULL = 100;

const ROW_STATE = Object.freeze({
    UPLOADING: 'uploading',
    SLOW: 'slow',
    OFFLINE: 'offline',
    CONFIRMING: 'confirming',
    SENT: 'sent',
    FAILED: 'failed',
});

const FIXED_TEXT = Object.freeze({
    [ROW_STATE.SLOW]: 'Conexão lenta. O envio continua sozinho.',
    [ROW_STATE.OFFLINE]: 'Sem internet. O envio continua quando a conexão voltar.',
    [ROW_STATE.CONFIRMING]: 'Conferindo o arquivo…',
    [ROW_STATE.SENT]: 'Enviado',
});

const megabytes = (bytes) => {
    const value = bytes / BYTES_PER_MB;
    if (value === 0) return '0';
    return value >= WHOLE_MB_FROM ? String(Math.round(value)) : value.toFixed(1).replace('.', ',');
};

const remainingText = (secondsLeft) => {
    if (!Number.isFinite(secondsLeft) || secondsLeft <= 0) return null;
    return secondsLeft >= SECONDS_PER_MINUTE
        ? `faltam ~${Math.ceil(secondsLeft / SECONDS_PER_MINUTE)} min`
        : `faltam ~${Math.ceil(secondsLeft)} s`;
};

const percentOf = (doc) => (typeof doc.uploadProgress === 'number' ? doc.uploadProgress : 0);

const uploadingText = (doc) => {
    const size = Number(doc.fileSize) || 0;
    const sent = `${megabytes((size * percentOf(doc)) / FULL)} de ${megabytes(size)} MB`;
    return ['Enviando', sent, remainingText(doc.secondsLeft)].filter(Boolean).join(' · ');
};

export const uploadRowText = (doc) => {
    if (doc.status === ROW_STATE.FAILED) return doc.failureMessage;
    return FIXED_TEXT[doc.status] || uploadingText(doc);
};

const STATE_ICON = Object.freeze({
    [ROW_STATE.SLOW]: { Icon: Clock, className: 'text-amber-600' },
    [ROW_STATE.OFFLINE]: { Icon: WifiOff, className: 'text-amber-600' },
    [ROW_STATE.SENT]: { Icon: CheckCircle2, className: 'text-green-600' },
    [ROW_STATE.FAILED]: { Icon: AlertCircle, className: 'text-red-600' },
});

const SPINNING = { Icon: Loader2, className: 'text-primary animate-spin' };

const isMoving = (status) => ![ROW_STATE.SENT, ROW_STATE.FAILED].includes(status);

const ProgressLine = ({ doc }) => (
    <div
        role='progressbar'
        aria-label={`Envio de ${doc.name}`}
        aria-valuemin={0}
        aria-valuemax={FULL}
        aria-valuenow={Math.round(percentOf(doc))}
        className='mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-200'
    >
        <div
            className={`h-full rounded-full transition-all duration-200 ${doc.status === ROW_STATE.UPLOADING ? 'bg-primary' : 'bg-amber-500'}`}
            style={{ width: `${percentOf(doc)}%` }}
        />
    </div>
);

const UploadRow = ({ doc, onRemove }) => {
    const { Icon, className } = STATE_ICON[doc.status] || SPINNING;
    const isFailed = doc.status === ROW_STATE.FAILED;
    return (
        <li className='flex items-center gap-3 border-b border-gray-100 bg-white py-2.5 pl-4 pr-3'>
            <Icon aria-hidden='true' className={`h-5 w-5 shrink-0 ${className}`} />
            <div className='min-w-0 flex-1'>
                <p className='truncate text-sm text-gray-900'>{doc.name}</p>
                <p className={`text-xs ${isFailed ? 'text-red-600' : 'text-gray-500'}`}>{uploadRowText(doc)}</p>
                {isMoving(doc.status) && <ProgressLine doc={doc} />}
            </div>
            {isFailed && (
                <button
                    type='button'
                    aria-label={`Remover ${doc.name}`}
                    className='rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    onClick={() => onRemove(doc._localId)}
                >
                    <X aria-hidden='true' className='h-4 w-4' />
                </button>
            )}
        </li>
    );
};

export default UploadRow;
